import type { PlatformAdapter } from '@rudi/api/platform';
import { TOKENS_KEY, clearSession, sessionKey } from './auth-storage';
import { apiBasePath, readTargetId } from './target';

/** Fixed per page load: the selected Rudi connection decides the API prefix and session. */
const TARGET_ID = readTargetId();
const API_BASE_PATH = apiBasePath(TARGET_ID);
const STORED_TOKENS_KEY = sessionKey(TOKENS_KEY, TARGET_ID);

/**
 * Cortex web PlatformAdapter.
 *
 * Self-contained (not shared with apps/web): cortex runs on its own origin, so
 * it keeps its own localStorage session. API calls go to the relative `/api`
 * path — the Vite dev proxy (dev) and server.ts (prod) forward it to the backend.
 */

interface StoredTokens {
  accessToken?: string | null;
  refreshToken?: string | null;
}

function readTokens(): StoredTokens {
  try {
    const raw = localStorage.getItem(STORED_TOKENS_KEY);
    return raw ? (JSON.parse(raw) as StoredTokens) : {};
  } catch {
    return {};
  }
}

function writeTokens(next: StoredTokens): void {
  try {
    localStorage.setItem(STORED_TOKENS_KEY, JSON.stringify(next));
  } catch {
    // Ignore storage errors.
  }
}

export const cortexAdapter: PlatformAdapter = {
  async getAccessToken(): Promise<string | null> {
    return readTokens().accessToken ?? null;
  },

  async setAccessToken(token: string | null): Promise<void> {
    writeTokens({ ...readTokens(), accessToken: token });
  },

  async getRefreshToken(): Promise<string | null> {
    return readTokens().refreshToken ?? null;
  },

  async setRefreshToken(token: string | null): Promise<void> {
    writeTokens({ ...readTokens(), refreshToken: token });
  },

  getApiBaseUrl(): string {
    return API_BASE_PATH;
  },

  getSocketUrl(): string {
    return typeof window !== 'undefined' ? window.location.origin : '';
  },

  async onSessionExpired(): Promise<void> {
    if (typeof window === 'undefined') return;
    clearSession();
    // The router guard renders the login screen when there is no session, so a
    // reload to the root is enough to bounce an expired user back to login.
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    } else {
      window.location.reload();
    }
  },

  isDevelopment(): boolean {
    try {
      return import.meta.env?.DEV ?? false;
    } catch {
      return false;
    }
  },
};
